import React, { useCallback, useEffect, useState } from "react";
import Map, {
  CircleLayer,
  Layer,
  LineLayer,
  LngLat,
  MapRef,
  Marker,
  NavigationControl,
  Source,
  Popup,
  SymbolLayer,
  FullscreenControl,
} from "react-map-gl";
import { LngLatBounds, MapboxGeoJSONFeature } from "mapbox-gl";
import {
  DictType,
  Endpoints,
  GeometryFeature,
  Project,
  Task,
  User,
  Wms,
} from "@pma/types";
// import AddAnnotation from "./annotation";
import AnnotationPreview from "./annotation-preview";
import { ACCESS_TOKEN, MAP_BOX_STYLE } from "@pma/utils/constants";
import { useAuth } from "@stores/auth-store";
import PmsLoader from "@components/common/loader";
import { toast } from "@hooks/use-toast";
import useTranslator from "@hooks/use-translator";
import { parseCookies } from "nookies";
import axios from "axios";
import {
  GET_WMS_ENDPOINT,
  SERVER_URL,
  UPDATE_WMS_ENDPOINT,
} from "@pma/utils/api-routes";
import PopupContent from "./map-popup";
import AnnotationControl from "./annotation-options";
import Legend from "./map-legend";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import AddTaskInput from "../projects/add-task-input";
import { useGisTasks } from "@pma/stores/tasks-store";
import TaskMarker from "./task-marker";
import { Separator } from "@components/common/separator";
import { ScrollArea } from "@components/common/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/common/accordion";
import { LocateIcon, PlusCircle, ZoomIn } from "lucide-react";
import { Button } from "@components/common/button";
import { getMidPoint, getWFSFeatures } from "@pma/utils/utils";
import AddAnnotation from "./add-annotation";

export interface LyrsTypes {
  layerName: string;
  data: any;
  geomType: string;
  customstyle: any;
}
export const drawControlOptions = {
  point: true,
};

interface Props {
  longitude: number;
  latitude: number;
  zoom: number;
  project: Project;
  refetch: CallableFunction;
}
const GisMap = ({ longitude, latitude, zoom, project, refetch }: Props) => {
  const mapReference = React.useRef<MapRef>(null);
  const [dataLayers, setDataLayers] = useState<LyrsTypes[]>();
  const [zoomLevel, setZoomLevel] = React.useState(zoom);
  const [cursor, setCursor] = React.useState<string>("pointer");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showMarkerPopUp, setShowMarkerPopUp] = React.useState<boolean>(false);
  const [endpointsData, setEndPointsData] = React.useState<Endpoints[]>([]);
  const [images] = useState<Array<string>>([
    "meters.png",
    "map_icon_one.png",
    "map_icon_two.png",
    "map_icon_three.png",
  ]);
  const [layerId, setlayerId] = React.useState<string>();
  const [activateLayer, setActivateLayer] = React.useState<string[]>();
  const [interactiveLayers, setInteractiveLayers] =
    React.useState(Array<string>());
  const [selectedFeature, setSelectedFeature] = React.useState<
    MapboxGeoJSONFeature | any | undefined | null
  >(null);
  const [midPoint, setMidPoint] = useState<DictType>({});
  const [lyrd, setlyrd] = React.useState<string | null>(
    window.localStorage.getItem("layerId") || null,
  );
  // const [userOptions, setUserOptions] = useState<Array<User>>(userOption);
  const [drawOptions, setDrawOptions] = React.useState(drawControlOptions);
  const [newAnnotation, setNewAnnotation] =
    React.useState<GeometryFeature | null>(null);
  const [showAnnotation, setShowAnnotation] = useState(false);
  const [annotations, setAnnotations] = useState(false);

  const [openCreateTask, setOpenCreateTask] = useState(false);

  const [addTaskInfo, setAddTaskInfo] = useState<DictType | null>(null);

  const [textAnnotation, setTextAnnotation] = React.useState<
    string | null | undefined
  >();

  const [AnnotationCollection, setAnnotationCollection] = React.useState<any>();

  const { authConfig } = useAuth();
  const { translate, locale } = useTranslator();
  const { gisTasks } = useGisTasks();

  const userId = authConfig?.id;
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const MAPBOX_TOKEN = process.env.REACT_APP_TOKEN;

  const mapAnnotations = project?.map_annotations;

  // Handle map click event
  const handleMapClick = useCallback((event: DictType) => {
    const { lng, lat } = event.lngLat;
    const features = event.features;
    if (features && features.length) {
      const clickedFeature = features[0];
      setSelectedFeature(clickedFeature);


      if (clickedFeature?.layer.id === "annotation-label") {
        setShowAnnotation(true);
        setNewAnnotation(features?.at(0) as any);
        setTextAnnotation(clickedFeature?.properties?.annotation);


      } else {
        setAddTaskInfo({
          longitude: lng,
          latitude: lat,
          details: clickedFeature,
        });
      }
    } else {


      toast({
        title: translate("Info"),
        description: translate("Select a feature to attach task"),
        variant: "info",
      });
      setAddTaskInfo(null); // Hide popup if no feature is clicked
    }
  }, []);

  const renderPopupContent = () => {
    if (!selectedFeature) return null;

    const properties = selectedFeature.properties || {};
    const keys = Object.keys(properties);

    return (
      <div className="p-4">
        <h4 className="font-bold mb-2">{selectedFeature.layer.id}</h4>
        <ul>
          {keys.map((key, index) => (
            <li key={index} className="mb-1">
              <span className="font-bold">{key}:</span> {properties[key]}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  useEffect(() => {
    axios
      .get(
        SERVER_URL +
          `${GET_WMS_ENDPOINT}?context_type=organization%20user&context_id=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((res) => {
        const d = res.data as Array<Endpoints>;

        setEndPointsData(d);
      })
      .catch((error) => {
      });
  }, []);

  useEffect(() => {
    const currentMap = mapReference.current?.getMap();

    if (currentMap && endpointsData) {
      setIsLoading(true);
      const bounds = currentMap.getBounds();

      const layerPromises = endpointsData.flatMap((r) =>
        r.wms.layers.map((x) => getWFSFeatures(bounds, x.name, r.wms.url)),
      );
      Promise.all(layerPromises)
        .then((results) => {
          setIsLoading(false);
          if (endpointsData.length === 0) {
            toast({
              title: translate("Info"),
              description: translate(
                "No map data url point found, go to settings and data url under GIS",
              ),
              variant: "info",
            });

            return;
          }
          const allEmpty = results.every(
            (collection) => collection.features.length === 0,
          );
          if (allEmpty) {
            toast({
              title: translate("Info"),
              description: translate(
                "No infrastructure data was found at this project location",
              ),
              variant: "info",
            });

            setIsLoading(false);
            return;
          }
          const data: any = [];

          const layerGeomType: any = [];
          const interactive_layers: string[] = [];

          endpointsData.map((r) => {
            r.wms.layers.forEach((x, index) => {
              if (index === results.length - 1) return;
              const handleData = (featuresIndex: number) => {
                const geomType =
                  results[featuresIndex].features[0].geometry.type;
                const stylings = layerStyleGenerator(x.name, geomType);

                if (x.geometryType === "") {
                  layerGeomType.push({
                    name: x.name,
                    geometryType: geomType,
                  });
                }
                interactive_layers.push(x.name);
                data.push({
                  layerName: x.name,
                  data: results[featuresIndex],
                  geomType: geomType,
                  customstyle: stylings,
                });
              };

              if (
                results[index].features.length === 0 &&
                index < results.length
              ) {
                handleData(index + 1);
              } else {
                handleData(index);
              }
            });
            setInteractiveLayers(interactive_layers);

            setDataLayers(data);

            currentMap.flyTo({ center: [longitude, latitude], zoom: 17.5 });
            setIsLoading(false);
            if (layerGeomType.length > 0) {
              var t: Wms = {
                url: r.wms.url,
                name: r.wms.name,
                type: r.wms.type,
                layers: layerGeomType,
              };
              const options: any = {
                wms_id: r.id,
                wms: t,
                organization_id: r.organization_id,
              };
              updateEndPoint(options);
            }
          });
        })
        .catch((error) => {
          setIsLoading(false);
        });
    }
  }, [endpointsData]);

  React.useEffect(() => {
    const currentMap = mapReference.current?.getMap();
    if (currentMap) {
      currentMap.on("load", () => {
        images.forEach((img) => {
          let imageUrl = `${process.env.PUBLIC_URL}/` + img;

          currentMap.loadImage(imageUrl, (error, image) => {
            if (error) throw error;
            var image_name = img.split(".").at(0);
            currentMap.addImage(image_name as string, image as ImageBitmap, {
              pixelRatio: 2,
            });
          });
        });
      });
    }
  }, [mapReference.current]);

  const updateEndPoint = (obj: any) => {
    axios
      .post(
        SERVER_URL + UPDATE_WMS_ENDPOINT,
        {
          wms_id: obj.wms_id,
          organization_id: obj.organization_id,
          wms: JSON.stringify(obj.wms),
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((res) => {})
      .catch((error) => {});
  };

  React.useEffect(() => {
    const currentMap = mapReference.current?.getMap();
    if (currentMap && layerId) {
      if (activateLayer?.includes(layerId)) {
        currentMap.setLayoutProperty(layerId, "visibility", "visible");
      } else {
        currentMap.setLayoutProperty(layerId, "visibility", "none");
      }
    }
  }, [mapReference, layerId, activateLayer]);

  const handleViewportChange = (newViewport: any) => {
    setZoomLevel(newViewport.viewState.zoom);
  };

  const layerPointStyle: CircleLayer = {
    id: "point",
    type: "circle",
    paint: {
      "circle-radius": 4,
      "circle-color": "#263af7",
    },
    source: "",
  };

  const layerStyleGenerator = (layerName: string, geomType: string) => {
    switch (geomType) {
      case "Point":
        if (layerName === "מד_מים") {
          return {
            id: layerName,
            type: "symbol",
            layout: {
              "icon-image": "map_icon_one",
              "icon-size": 2.5,
              "icon-allow-overlap": true,
            },
            source: "",
          } as SymbolLayer;
        } else if (layerName === "מגוף") {
          return {
            id: layerName,
            type: "symbol",
            layout: {
              "icon-image": "meters",
              "icon-size": 2.5,
              "icon-allow-overlap": true,
            },
            source: "",
          } as SymbolLayer;
        } else if (layerName === "ברז_כיבוי_אש") {
          return {
            id: layerName,
            type: "symbol",
            layout: {
              "icon-image": "map_icon_three",
              "icon-size": 2.5,
              "icon-allow-overlap": true,
            },
            source: "",
          } as SymbolLayer;
        }

        break;
      case "MultiLineString":
        if (layerName === "קו_ביוב") {
          return {
            id: layerName,
            type: "line",
            paint: {
              "line-width": 2,
              "line-color": "red",
            },
            source: "",
          } as LineLayer;
        } else if (layerName === "מתאם") {
          return {
            id: layerName,
            type: "line",
            paint: {
              "line-width": 2,
              "line-color": "#ADD8E6",
            },
            source: "",
          } as LineLayer;
        } else {
          return {
            id: layerName,
            type: "line",
            paint: {
              "line-width": 2,
              "line-color": "blue",
            },
            source: "",
          } as LineLayer;
        }
      default:
        break;
    }
  };
  const onMouseEnter = useCallback(() => setCursor("pointer"), []);
  const onMouseLeave = useCallback(() => setCursor("auto"), []);

  React.useEffect(() => {
    const currentMap = mapReference.current?.getMap();
    // setlyrd(window.localStorage.getItem("layerId"))

    if (!currentMap) {
      // Exit if mapRef not found
      return;
    }

    const selectedFeatureId = selectedFeature?.properties?.OBJECTID;

    const layerId = selectedFeatureId ? `selected-${selectedFeatureId}` : "";

    if (selectedFeature) {
      if (selectedFeature.geometry.type === "Point") {
        setMidPoint([
          selectedFeature.geometry.coordinates?.at(0) as number,
          selectedFeature.geometry.coordinates?.at(1) as number,
        ]);

        currentMap.addLayer({
          id: layerId,
          type: "circle",
          source: `${selectedFeature.layer.source}`,
          paint: {
            "circle-color": [
              "case",
              ["==", ["get", "OBJECTID"], selectedFeatureId],
              "#ff0000", // Color for selected feature
              "#f6f6f6", // Default color
            ],
            "circle-radius": 5,
            "circle-stroke-color": "#333333",
            "circle-stroke-width": 1,
          },
        });
      } else if (
        selectedFeature.geometry.type === "LineString" ||
        selectedFeature.geometry.type === "MultiLineString"
      ) {
        const location = getMidPoint(selectedFeature.geometry.coordinates);
        const source = selectedFeature.layer.source;
        // setMidPoint(location);

        if (selectedFeatureId) {
          currentMap.addLayer({
            id: layerId,
            type: "line",
            source: `${selectedFeature.layer.source}`,
            paint: {
              "line-color": [
                "case",
                ["==", ["get", "OBJECTID"], selectedFeatureId],
                "yellow", // Color for selected feature
                "red",
              ],
              "line-width": 5,
            },
          });
        } else {
          currentMap.addLayer({
            id: layerId,
            type: "line",
            source: `${selectedFeature.layer.source}`,
            paint: {
              "line-color": "blue", // Default color
              "line-width": 5,
            },
          });
        }
      }
    } else {
      if (lyrd && project) {
        currentMap.setFilter(lyrd, ["==", ["get", "OBJECTID"], ""]);
      }
    }
  }, [selectedFeature]);

  //  feature attribute information
  // const FeatureAttributeInfo = () => {
  //   let attribute_names = Object.keys(selectedFeature.properties);
  //   let attribute_values = Object.values(selectedFeature.properties);

  //   return (
  //     <React.Fragment>
  //       <div className="">
  //         <div>
  //           {attribute_names.map((a, index) => (
  //             <p className="labels" key={index}>
  //               {a}
  //             </p>
  //           ))}
  //         </div>
  //         <div>
  //           {attribute_values.map((a, index) => (
  //             <p dangerouslySetInnerHTML={{ __html: a as any }} key={index}></p>
  //           ))}
  //         </div>
  //       </div>
  //     </React.Fragment>
  //   );
  // };

  const onUpdate = React.useCallback((e: any) => {
    if (e.features) {
      const f: GeometryFeature = e.features.at(0);
      if (f.geometry.type === "Point") {
        setNewAnnotation(e.features.at(0));
      } else {
        // console.log(f.geometry.type)
      }
    }
  }, []);

  const onDelete = React.useCallback(() => {}, []);

  React.useEffect(() => {
    if (newAnnotation) {
      setShowAnnotation(true);
    }
  }, [showAnnotation, newAnnotation]);

  // annotation style
  const labelText: SymbolLayer = {
    id: "annotation-label",
    type: "symbol",
    source: "",
    layout: {
      "text-field": ["get", "annotation"],
      "text-size": 14,
      "text-offset": [0, 0],
      "icon-image": "bus-15",
    },
    paint: {
      "text-color": "#f30707",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1,
    },
  };

  // React.useEffect(() => {
  //   const collection = turf.helpers.featureCollection(annotations as any);

  //   setAnnotationCollection(collection);
  // }, [annotations, project]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="flex items-center w-full h-full min-h-[20vh] justify-center">
        {translate("API Key Not Found")}
      </div>
    );
  }

  return (
    <Map
      mapboxAccessToken={MAPBOX_TOKEN}
      initialViewState={{
        longitude: longitude,
        latitude: latitude,
        zoom: zoomLevel,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onZoom={handleViewportChange}
      style={{ height: "100%", borderRadius: "15px" }}
      mapStyle={MAP_BOX_STYLE}
      ref={mapReference}
      interactive
      interactiveLayerIds={[...interactiveLayers, "annotation-label"]}
      cursor={cursor}
      onClick={handleMapClick}
      locale={{ locale }}
    >
      <Marker
        longitude={longitude}
        latitude={latitude}
        onClick={(e) => {
          // If we let the click event propagates to the map, it will immediately close the popup
          // with `closeOnClick: true`
          e.originalEvent.stopPropagation();
          setShowMarkerPopUp(true);
        }}
        style={{ cursor: "pointer" }}
      >
        <img
          className={"h-16 w-16"}
          src={`${process.env.PUBLIC_URL}/marker-2.png`}
        />
        {showMarkerPopUp && (
          <Popup
            latitude={latitude}
            longitude={longitude}
            onClose={() => setShowMarkerPopUp(false)}
          >
            <PopupContent project={project} />
          </Popup>
        )}
      </Marker>

      {dataLayers && (
        <>
          {dataLayers?.map((x, index) => (
            <Source id={x.layerName} type="geojson" key={index} data={x.data}>
              <Layer {...x.customstyle} />
            </Source>
          ))}
        </>
      )}

      {gisTasks && (
        <>
          {gisTasks?.map((task, index) => (
            <TaskMarker task={task} key={index} />
          ))}
        </>
      )}
      {addTaskInfo && (
        <Popup
          longitude={addTaskInfo?.longitude}
          latitude={addTaskInfo?.latitude}
          onClose={() => {
            setAddTaskInfo(null);
            setOpenCreateTask(false);
          }}
          closeOnClick={false}
          style={{
            padding: 0,
            margin: 0,
          }}
        >
          <div className="flex gap-3 font-semibold text-lg">
            <span className="">Layer :</span>
            <p>
              {addTaskInfo?.details?.layer?.id
                .replaceAll("-", " ")
                .replaceAll("_", " ")}
            </p>
          </div>

          <div className="flex gap-4">
            <span className="flex items-center gap-1">
              <div className="h-3 w-3 bg-yellow-300 rounded-full" />
              {translate("Selected")}
            </span>
            <span className="flex items-center gap-1">
              <div className="h-3 w-3 bg-red-500 rounded-full" />
              {translate("Other")}
            </span>
          </div>
          <div className="w-[300px]">
            <div className="flex gap-2 my-2">
              <Button
                size={"sm"}
                className="bg-primary/70 text-white"
                variant={"outline"}
                leftSection={<ZoomIn size={14} />}
                onClick={() => {
                  const currentMap = mapReference.current?.getMap();

                  currentMap?.flyTo({
                    center: [
                      addTaskInfo?.longitude as number,
                      addTaskInfo?.latitude as number,
                    ],
                    zoom: 22,
                  });
                }}
              >
                {translate("Zoom to layers")}
              </Button>
              <Button
                size={"sm"}
                className=""
                variant={openCreateTask ? "ghost" : "outline"}
                onClick={() => setOpenCreateTask(!openCreateTask)}
                leftSection={
                  openCreateTask ? (
                    <LocateIcon size={14} />
                  ) : (
                    <PlusCircle size={14} />
                  )
                }
              >
                {translate(openCreateTask ? "Point Details" : "Add new Task")}
              </Button>
            </div>

            {openCreateTask ? (
              <>
                <AddTaskInput
                  projectId={String(project?.id)}
                  gisData={addTaskInfo}
                  closeGisPopUp={setAddTaskInfo}
                />
                <div className="flex gap-3">
                  <div className="">
                    <span className="flex flex-col gap-[1px]">
                      {addTaskInfo?.longitude}
                    </span>
                    <small className="text-muted-foreground">
                      {translate("Longitude")}
                    </small>
                  </div>
                  <div className="flex flex-col gap-[1px]">
                    <span>{addTaskInfo?.latitude}</span>
                    <small className="text-muted-foreground">
                      {translate("Latitude")}
                    </small>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Separator />
                <Accordion
                  defaultValue="properties"
                  type="single"
                  collapsible
                  className="w-full"
                >
                  {addTaskInfo?.details?.layer && (
                    <AccordionItem value="layers">
                      <AccordionTrigger>{translate("Layer")}</AccordionTrigger>
                      <AccordionContent>
                        {Object.keys(addTaskInfo?.details?.layer)?.map(
                          (attr, index) => (
                            <div
                              key={index}
                              className="flex w-full justify-between"
                            >
                              <div className="capitalize font-medium border flex-1 p-1">
                                {attr
                                  .toLocaleLowerCase()
                                  .replaceAll("_", " ")
                                  .replaceAll("-", " ")}
                              </div>

                              <div className="text-left border flex-1 p-1">
                                {typeof addTaskInfo?.details?.layer[
                                  `${attr}`
                                ] == "object"
                                  ? "---"
                                  : addTaskInfo?.details?.layer[`${attr}`]}
                              </div>
                            </div>
                          ),
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {addTaskInfo?.details?.geometry && (
                    <AccordionItem value="geometry">
                      <AccordionTrigger>
                        {translate("Geometry")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex w-full justify-between">
                          <div className="capitalize font-medium border flex-1 p-1">
                            {translate("Longitude")}
                          </div>

                          <div className="text-left border flex-1 p-1">
                            {addTaskInfo?.longitude}
                          </div>
                        </div>
                        <div className="flex w-full justify-between">
                          <div className="capitalize font-medium border flex-1 p-1">
                            {translate("Latitude")}
                          </div>

                          <div className="text-left border flex-1 p-1">
                            {addTaskInfo?.latitude}
                          </div>
                        </div>
                        {Object.keys(addTaskInfo?.details?.geometry)?.map(
                          (attr, index) => (
                            <div
                              key={index}
                              className="flex w-full justify-between"
                            >
                              <div className="capitalize font-medium border flex-1 p-1">
                                {attr
                                  .toLocaleLowerCase()
                                  .replaceAll("_", " ")
                                  .replaceAll("-", " ")}
                              </div>

                              <div className="text-left border flex-1 p-1">
                                {typeof addTaskInfo?.details?.geometry[
                                  `${attr}`
                                ] == "object"
                                  ? "---"
                                  : addTaskInfo?.details?.geometry[`${attr}`]}
                              </div>
                            </div>
                          ),
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  <AccordionItem value="properties">
                    <AccordionTrigger>
                      {translate("Properties")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <ScrollArea className="flex flex-col h-[400px] pb-6">
                        {addTaskInfo?.details?.properties ? (
                          <>
                            {Object.keys(addTaskInfo?.details?.properties)?.map(
                              (attr, index) => (
                                <div
                                  key={index}
                                  className="flex w-full justify-between"
                                >
                                  <div className="capitalize font-medium border flex-1 p-1">
                                    {attr
                                      .toLocaleLowerCase()
                                      .replaceAll("_", " ")
                                      .replaceAll("-", " ")}
                                  </div>

                                  <div className="text-left border flex-1 p-1">
                                    {typeof addTaskInfo?.details?.properties[
                                      `${attr}`
                                    ] == "object"
                                      ? "---"
                                      : addTaskInfo?.details?.properties[
                                          `${attr}`
                                        ]}
                                  </div>
                                </div>
                              ),
                            )}
                          </>
                        ) : (
                          <>
                            {/* This is only there to account for previously created records 15-01-2025 */}
                            {addTaskInfo?.details &&
                              Object.keys(addTaskInfo?.details)?.map(
                                (attr, index) => (
                                  <div
                                    key={index}
                                    className="flex w-full justify-between"
                                  >
                                    <div className="capitalize font-medium border flex-1 p-1">
                                      {attr
                                        .toLocaleLowerCase()
                                        .replaceAll("_", " ")
                                        .replaceAll("-", " ")}
                                    </div>

                                    <div className="text-left border flex-1 p-1">
                                      {typeof addTaskInfo?.details[`${attr}`] ==
                                      "object"
                                        ? "---"
                                        : addTaskInfo?.details[`${attr}`]}
                                    </div>
                                  </div>
                                ),
                              )}
                          </>
                        )}
                      </ScrollArea>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </>
            )}
          </div>
        </Popup>
      )}

      {showAnnotation && newAnnotation && project && (
        <Popup
          latitude={newAnnotation.geometry.coordinates?.at(1) as number}
          longitude={newAnnotation.geometry.coordinates?.at(0) as number}
          closeOnClick={false}
          onClose={() => {
            setShowAnnotation(false);
            setNewAnnotation(null);
          }}
        >
          {showAnnotation && (
            <AddAnnotation
              setShowAnnotationPanel={setShowAnnotation}
              mapRef={mapReference}
              setAnnotations={setNewAnnotation}
              textAnnotations={textAnnotation}
              refetch={refetch}
              projectId={project.id}
              annotation={newAnnotation}
            />
          )}
        </Popup>
      )}

      {mapAnnotations?.map((annotation) => (
        <AnnotationPreview annotation={annotation} refetch={refetch} />
      ))}
      {/* 
      {midPoint?.id && (
        <Popup
          longitude={midPoint[0]}
          latitude={midPoint[1]}
          // onClose={closePopup}
        >
          {renderPopupContent()}
        </Popup>
      )} */}

      <NavigationControl />
      {isLoading && <PmsLoader />}
      <AnnotationControl
        displayControlsDefault={true}
        controls={drawOptions}
        onCreate={onUpdate}
        onDelete={onDelete}
      />
      <FullscreenControl />

      <Legend
        mapRef={mapReference}
        layersInformation={dataLayers as Array<LyrsTypes>}
        setLayerId={setlayerId}
        setActiveLayers={setActivateLayer}
      />
      {AnnotationCollection && (
        <Source
          id="annotation-layer"
          data={AnnotationCollection}
          type="geojson"
        >
          <Layer {...labelText} />
        </Source>
      )}
    </Map>
  );
};

export default GisMap;
